import { useMemo, type CSSProperties } from 'react';
import { Button, Flex, Modal, Typography } from 'antd';
import { aboveEverythingZIndex } from '@/shared/ui/FloatingWindow';
import { DialogFooter } from '@/shared/ui/DialogFooter';
import { BottomSheet } from '@/shared/ui/BottomSheet';
import { ResponsiveTable } from '@/shared/ui/ResponsiveTable';
import type { ResponsiveColumns } from '@/shared/ui/ResponsiveTable';
import { useBreakpoint } from '@/shared/lib/hooks';
import type { PreviewAffectedDocument, PreviewBalanceChange, PreviewDiff, PreviewPaymentChange, PreviewStockChange } from '@/entities/document';
import { affectedNotes, paymentChangeText } from './model';

const PAYMENT_STATUS_LABEL: Record<string, string> = { UNPAID: 'не оплачено', PARTIAL: 'частично', PAID: 'оплачено' };

export interface PreviewConsequencesDialogProps {
  diff: PreviewDiff | undefined;
  /** Предупреждения, с которыми документ будет сохранён, — уже словами (названия товаров знает
   * только окно документа). */
  warningTexts?: string[];
  onResolve: (confirmed: boolean) => void;
}

// `mobile` — роль столбца на карточке телефона; на десктопе столбцы остаются прежними.
const stockColumns: ResponsiveColumns<PreviewStockChange> = [
  { title: 'Товар', dataIndex: 'categoryName', mobile: 'title' },
  { title: 'Было', width: 100, align: 'right', render: (_v, r) => r.before.qtyTotal },
  { title: 'Станет', width: 100, align: 'right', render: (_v, r) => r.after.qtyTotal },
];

const documentColumns: ResponsiveColumns<PreviewAffectedDocument> = [
  { title: 'Документ', dataIndex: 'label', width: 120, mobile: 'title' },
  { title: 'Сумма', width: 160, render: (_v, r) => `${r.totalAmountBefore} → ${r.totalAmountAfter}` },
  { title: 'Себестоимость', width: 160, render: (_v, r) => `${r.costBefore} → ${r.costAfter}` },
  {
    title: 'Статус оплаты',
    width: 160,
    render: (_v, r) => `${PAYMENT_STATUS_LABEL[r.paymentStatusBefore] ?? r.paymentStatusBefore} → ${PAYMENT_STATUS_LABEL[r.paymentStatusAfter] ?? r.paymentStatusAfter}`,
  },
  { title: 'Что ещё изменится', mobile: 'subtitle', render: (_v, r) => affectedNotes(r).join('; ') || '—' },
];

const paymentColumns: ResponsiveColumns<PreviewPaymentChange> = [
  { title: 'Платёж', dataIndex: 'label', mobile: 'title' },
  { title: 'Контрагент', dataIndex: 'counterpartyName', width: 180, mobile: 'subtitle' },
  { title: 'Что произойдёт', width: 260, mobile: 'subtitle', render: (_v, r) => paymentChangeText(r) },
];

const balanceColumns: ResponsiveColumns<PreviewBalanceChange> = [
  { title: 'Контрагент', dataIndex: 'counterpartyName', mobile: 'title' },
  { title: 'Было', width: 120, align: 'right', render: (_v, r) => r.before },
  { title: 'Станет', width: 120, align: 'right', render: (_v, r) => r.after },
];

/** §8.5: «Остаток товара X станет −10 шт» / «Затронуты документы...» / «Баланс контрагента Y:
 * было → станет» — предупреждение, а не блокировка (кнопки «Сохранить»/«Отмена»).
 * На телефоне — нижний лист на весь экран: таблицы карточками, крупные кнопки подтверждения. */
export function PreviewConsequencesDialog({ diff, warningTexts = [], onResolve }: PreviewConsequencesDialogProps) {
  const { isMobile } = useBreakpoint();
  const isOpen = diff !== undefined;
  // Плавающие окна документов используют собственный растущий z-index (shared/ui/FloatingWindow) —
  // выше базового antd-модального (1000), поэтому обычный Modal рисуется ПОД открытым окном
  // документа. Берём следующий z-index из того же счётчика при каждом открытии диалога, чтобы
  // он гарантированно оказался поверх любого уже открытого окна.
  const zIndex = useMemo(() => (isOpen ? aboveEverythingZIndex() : undefined), [isOpen]);

  // Телефон: подпись раздела — отдельной строкой над карточками, после карточек — отступ.
  const heading: CSSProperties | undefined = isMobile ? { display: 'block', marginBottom: 6 } : undefined;
  const gap = isMobile ? <div style={{ height: 16 }} /> : null;

  const content = (
    <>
      {warningTexts.length > 0 ? (
        <>
          <Typography.Text strong style={heading}>
            Предупреждения
          </Typography.Text>
          <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 18 }}>
            {warningTexts.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </>
      ) : null}
      {diff && diff.stockChanges.length > 0 ? (
        <>
          <Typography.Text strong style={heading}>
            Остатки товаров
          </Typography.Text>
          <ResponsiveTable<PreviewStockChange> size="small" rowKey="categoryId" dataSource={diff.stockChanges} columns={stockColumns} pagination={false} style={{ marginBottom: 16 }} />
          {gap}
        </>
      ) : null}
      {diff && diff.affectedDocuments.length > 0 ? (
        <>
          <Typography.Text strong style={heading}>
            Затронутые документы
          </Typography.Text>
          <ResponsiveTable<PreviewAffectedDocument>
            size="small"
            rowKey="documentId"
            dataSource={diff.affectedDocuments}
            columns={documentColumns}
            pagination={false}
            style={{ marginBottom: 16 }}
          />
          {gap}
        </>
      ) : null}
      {diff && diff.balances.length > 0 ? (
        <>
          <Typography.Text strong style={heading}>
            Баланс контрагентов
          </Typography.Text>
          <ResponsiveTable<PreviewBalanceChange>
            size="small"
            rowKey="counterpartyId"
            dataSource={diff.balances}
            columns={balanceColumns}
            pagination={false}
            style={{ marginBottom: 16 }}
          />
          {gap}
        </>
      ) : null}
      {diff && diff.payments.length > 0 ? (
        <>
          <Typography.Text strong style={heading}>
            Платежи
          </Typography.Text>
          <ResponsiveTable<PreviewPaymentChange> size="small" rowKey="paymentId" dataSource={diff.payments} columns={paymentColumns} pagination={false} />
        </>
      ) : null}
    </>
  );

  if (isMobile) {
    return (
      <BottomSheet
        open={isOpen}
        onClose={() => onResolve(false)}
        title="Последствия изменения"
        height="full"
        zIndex={zIndex}
        footer={
          <Flex gap={8}>
            <Button size="large" onClick={() => onResolve(false)} style={{ flex: '0 0 35%', minHeight: 48 }}>
              Отмена
            </Button>
            <Button type="primary" size="large" onClick={() => onResolve(true)} style={{ flex: 1, minHeight: 48 }}>
              Сохранить
            </Button>
          </Flex>
        }
      >
        {content}
      </BottomSheet>
    );
  }

  return (
    <Modal
      title="Последствия изменения"
      open={isOpen}
      onCancel={() => onResolve(false)}
      // Курсор на «Сохранить»: диалог — часть сохранения с клавиатуры, Enter подтверждает.
      footer={<DialogFooter okText="Сохранить" onOk={() => onResolve(true)} onCancel={() => onResolve(false)} />}
      destroyOnHidden
      width={860}
      zIndex={zIndex}
    >
      {content}
    </Modal>
  );
}
